// Board CHROME / place-identity — banner, per-rung theme, motif field + frame ornament.
// Sub-split from boardView.ts (FACE&FURY S6, behavior-frozen): bodies byte-identical to the
// monolith; only the containing file changed. Bound onto GameScene via the boardView.ts barrel.
import Phaser from 'phaser';
import type { Snapshot } from '../../core';
import { PALETTE, hex, FONT, tex, hasTex, type LogicalKey } from '../keys';
import { motionAllowed, scaledFont } from '../settings';
import { isLandscape, LANDSCAPE_GUTTER_W } from '../stage';
import { typePx } from '../ui/typeScale';
import { BOARD_THEMES, mixColor, motifRand, type BoardTheme } from './types';
import type { GameScene } from '../scenes/GameScene';

// ── S6 TOPIC WORLDS ─────────────────────────────────────────────────────────────────────────────
// Per-bucket ambient backdrop painted UNDER a heavy black legibility scrim, BEHIND the grid. Buckets
// are resolved from the board's topic via a keyword map; anything unmatched (and the reduceMotion
// path's atmosphere) falls back to SCIENCE — the producer's "STRONGEST / darkest / most even, zero
// label risk, universal enough to double as default" verdict (LEDGER topic-worlds). ARCADE is kept as
// its OWN bucket for gaming/entertainment topics (its synthwave grid-horizon is deliberately louder,
// so it is not the neutral catch-all). Decoration only — never reads Core state.
export type WorldBucket = 'geography' | 'music' | 'science' | 'sports' | 'arcade';
/** The DEFAULT / fallback bucket (documented pick: science over arcade — arcade's synthwave grid is
 *  too loud for a neutral default; science is the darkest, most even, universal field). */
const WORLD_DEFAULT: WorldBucket = 'science';
/** First matching bucket wins (checked in this order). Keywords are matched WORD-BOUNDARY-aware
 *  (matchWorldBucket) against the lowercased topic slug + label, so "World Capitals", "NBA Teams",
 *  "Rock Bands" route correctly while 'team' no longer matches 's-TEAM' and 'map' no longer matches
 *  'maple'. Loose whole-word tokens that can't be disambiguated by a boundary ('rock' → geology's
 *  "Rock Formations", 'player' → any media "player") are DROPPED — their buckets have ample other
 *  tokens (music: band/song/genre…; sports: team/league/athlet…). Design STEMS (>=4 chars, e.g.
 *  'geograph', 'countr', 'athlet') still match as a word PREFIX. */
const WORLD_KEYWORDS: ReadonlyArray<readonly [WorldBucket, readonly string[]]> = [
  ['music', ['music', 'song', 'band', 'album', 'artist', 'singer', 'rapper', 'instrument', 'genre', 'pop', 'hip hop', 'jazz', 'lyric', 'composer', 'guitar', 'piano', 'orchestra', 'concert', 'billboard']],
  ['sports', ['sport', 'football', 'soccer', 'basketball', 'baseball', 'tennis', 'olympic', 'athlet', 'nba', 'nfl', 'nhl', 'mlb', 'hockey', 'golf', 'cricket', 'boxing', 'rugby', 'league', 'team', 'champion', 'tournament', 'wrestl']],
  ['geography', ['geograph', 'world', 'countr', 'capital', 'flag', 'map', 'city', 'cities', 'river', 'mountain', 'continent', 'landmark', 'travel', 'nation', 'ocean', 'border', 'europe', 'africa', 'asia', 'america', 'state']],
  ['arcade', ['game', 'gaming', 'video game', 'arcade', 'movie', 'film', 'tv', 'television', 'anime', 'comic', 'cartoon', 'pop culture', 'entertain', 'celebrit', 'disney', 'marvel', 'star wars', 'meme', 'internet']],
  ['science', ['science', 'physic', 'chemist', 'biolog', 'space', 'astronom', 'planet', 'nature', 'animal', 'body', 'technolog', 'math', 'medic', 'element', 'atom', 'star', 'galaxy', 'dinosaur', 'invention', 'computer', 'brain']],
];

/**
 * Deterministic topic→bucket resolver (first bucket wins, no Core read). Word-boundary aware so a
 * keyword only matches on a word boundary — non-letters (::, _, -, digits) collapse to spaces in BOTH
 * the haystack AND the keyword, so 'team' no longer matches 'steam', 'map' no longer matches 'maple',
 * and 'hip-hop' / 'hip hop' resolve identically. Multi-word phrase keywords ('star wars') match as a
 * bounded phrase; single-word keywords match a whole word, its simple plural, OR (for STEMS >=4 chars)
 * a word PREFIX so 'geograph' still catches 'geography' and 'countr' still catches 'countries'.
 *
 * Exported (S9 FIX-C): the GET READY world PILL needs the STRICT resolve — `undefined` when nothing
 * genuinely matches — so the science fallback is never stamped as a wrong label on an unmapped topic
 * (backdrop/accent consumers keep using boardWorldBucket's documented fallback).
 */
export function matchWorldBucket(rawHay: string): WorldBucket | undefined {
  const norm = (s: string): string => s.toLowerCase().replace(/[^a-z]+/g, ' ').trim();
  const padded = ` ${norm(rawHay)} `;
  const words = padded.split(' ').filter(Boolean);
  for (const [bucket, keys] of WORLD_KEYWORDS) {
    for (const raw of keys) {
      const k = norm(raw);
      if (!k) continue;
      if (k.includes(' ')) {
        if (padded.includes(` ${k} `)) return bucket; // bounded multi-word phrase
      } else if (words.some((w) => w === k || (k.length >= 4 ? w.startsWith(k) : w === `${k}s`))) {
        return bucket;
      }
    }
  }
  return undefined;
}

/**
 * Per-bucket FLAT legibility scrim alpha over the topic-world backdrop (paintBoardWorld). M4/#27 pushes
 * world PRESENCE HARD at the screen edges + landscape gutters (the cabinet-mock ceiling), so this flat
 * scrim is LOWERED (quiet worlds 0.48→0.38, arcade 0.55→0.46) to brighten the world in the open margins;
 * paintBoardWorld then re-deepens ONLY the board region with a soft center-weighted dark POOL so the
 * PLAY AREA stays calm + high-contrast (net board-region scrim ≈ 0.60/0.68, above the old 0.48/0.55).
 * The board panel (0.72 surface) + opaque tile faces sit ABOVE all of this, so a tile label never
 * composites against the world (>=4.5:1 held; the pool keeps the immediate surround + RULE band safe).
 */
const WORLD_SCRIM: Record<WorldBucket, number> = {
  geography: 0.38,
  music: 0.38,
  science: 0.38,
  sports: 0.38,
  arcade: 0.46,
};
/** Logical texture key per bucket + orientation (P=portrait, L=landscape). */
const WORLD_TEX: Record<WorldBucket, { P: LogicalKey; L: LogicalKey }> = {
  geography: { P: 'worldGeographyP', L: 'worldGeographyL' },
  music: { P: 'worldMusicP', L: 'worldMusicL' },
  science: { P: 'worldScienceP', L: 'worldScienceL' },
  sports: { P: 'worldSportsP', L: 'worldSportsL' },
  arcade: { P: 'worldArcadeP', L: 'worldArcadeL' },
};
/** Bucket accent (used to tint the GET READY card + board frame so chrome agrees with the world). */
export const WORLD_ACCENT: Record<WorldBucket, number> = {
  geography: 0x5eead4, // teal contours
  music: 0xe879f9, // magenta spectrum
  science: 0x8b9cff, // violet-cyan orbitals
  sports: 0x7dd3fc, // cool floodlight
  arcade: 0xff9edb, // synthwave magenta
};



export function buildBanner(this: GameScene, snap: Snapshot): void {
    // Tactical-OLED rule banner: a surface bar with a 3px scan LEFT border, an eyebrow "RULE" (scan)
    // over the rule text (Chakra 700), and the pause iconButton docked at the bar's right edge.
    const W = this.scale.width;
    const bannerH = Math.min(72, this.scale.height * 0.1);
    // Landscape: the rule bar spans the CENTRE board column (between the two gutter rails), not the full
    // 1280 width — so it caps the board like a marquee instead of crossing the telemetry/command rails.
    const marginX = isLandscape() ? LANDSCAPE_GUTTER_W : Math.max(8, W * 0.03);
    const barX = marginX;
    const barW = W - marginX * 2;
    const barY = Math.max(6, bannerH * 0.12);
    const barH = bannerH - barY - 4;
    const cy = barY + barH / 2;
    const rad = 12;

    const g = this.add.graphics().setDepth(1);
    g.fillStyle(PALETTE.surface, 1);
    g.fillRoundedRect(barX, barY, barW, barH, rad);
    // Stronger plate edge (Gemini P2 readability): the rule is the topic-forward hero, so the bar gets
    // a crisper, more visible border to lift it off the board field behind it.
    g.lineStyle(1.5, PALETTE.lineStrong, 1);
    g.strokeRoundedRect(barX, barY, barW, barH, rad);
    // 3px scan accent on the LEFT edge of the bar.
    g.fillStyle(PALETTE.scan, 1);
    g.fillRoundedRect(barX, barY + 3, 3, barH - 6, { tl: 2, bl: 2, tr: 0, br: 0 });
    // bannerBg retained (read by the entrance flourish) as an invisible hit-free anchor over the bar.
    this.bannerBg = this.add.rectangle(W / 2, cy, barW, barH, 0x000000, 0).setDepth(1);

    const pauseSize = Math.max(30, Math.min(40, barH - 8));
    const textX = barX + 14;
    const textRight = barX + barW - pauseSize - 18;
    // Boxed "RULE" tag (scan border + faint scan fill + scan text), INLINE with the rule text — matches
    // the R01 reference (was a stacked eyebrow). Vertically centered in the bar.
    const tagFs = Math.max(10, Math.min(13, W * 0.028));
    const tagText = this.add
      .text(0, cy, 'RULE', { fontFamily: FONT.display, fontSize: `${typePx(this, 'micro', { vw: 0.028, min: 10, max: 13 })}px`, color: hex(PALETTE.scan), fontStyle: '700' })
      .setOrigin(0, 0.5)
      .setDepth(2);
    const tagPadX = 7;
    const tagW = tagText.width + tagPadX * 2;
    const tagH = tagFs + 8;
    const tagG = this.add.graphics().setDepth(2);
    tagG.fillStyle(PALETTE.scan, 0.12);
    tagG.fillRoundedRect(textX, cy - tagH / 2, tagW, tagH, 5);
    tagG.lineStyle(1.25, PALETTE.scan, 0.9);
    tagG.strokeRoundedRect(textX, cy - tagH / 2, tagW, tagH, 5);
    tagText.setX(textX + tagPadX);
    // Rule text inline to the right of the tag, vertically centered, single line, shrink-to-fit (no
    // two-line wrap that would crowd the top safe area on long rules).
    const ruleX = textX + tagW + 10;
    this.bannerRestX = ruleX;
    const ruleAvail = Math.max(40, textRight - ruleX);
    // Keep the rule READABLE: wrap long rules to (at most) 2 lines at a fixed legible size rather than
    // shrinking to an illegible single line. Vertically centered next to the boxed RULE tag.
    // Rule now authored short (<=58 chars), so it can render LARGER and stay one line — instantly
    // readable at arm's length on a phone. Shrink-to-fit (below) still protects any long M1 rule.
    // Bumped a notch + routed through scaledFont (Gemini P2 + a11y text-scale): the hero rule reads
    // bigger and now respects the text-size setting. The 23px cap keeps it from over-growing on tablet/
    // desktop; bannerH is a FIXED constant, so a larger font NEVER pushes the grid down. The shrink-to-
    // fit loop below still caps any long rule at 2 lines, so the bump can't wrap into the grid at 320–360px.
    const ruleFsMax = typePx(this, 'title', { vw: 0.055, min: 18, max: 25 });
    const ruleFsMin = scaledFont(14, 12);
    this.bannerRule = this.add
      .text(ruleX, cy, snap.rule, {
        fontFamily: FONT.display,
        fontSize: `${ruleFsMax}px`,
        color: hex(PALETTE.white),
        fontStyle: 'bold',
        align: 'left',
        wordWrap: { width: ruleAvail },
        maxLines: 2,
      })
      .setOrigin(0, 0.5)
      .setDepth(2)
      // Soft dark halo lifts the white rule off the plate on any board background (static; a11y-safe).
      .setShadow(0, 1, '#0a0c14', 3, false, true);
    // Shrink-to-fit safety net: NEVER truncate the win-condition. Seed rules are now authored short, but
    // a long topic/generated rule (M1+) must still fit — drop the font a point at a time until the rule
    // wraps to <=2 lines (or hits the legibility floor). No-op for the short seed rules.
    for (let fs = ruleFsMax; fs > ruleFsMin; fs -= 1) {
      if (this.bannerRule.getWrappedText(snap.rule).length <= 2) break;
      this.bannerRule.setFontSize(fs - 1);
    }
    // A two-line rule can still be too tall at 150% text scale. Preserve the full rule text, but keep
    // the fixed marquee from crowding its rounded frame or the pause button.
    for (let fs = Number.parseFloat(String(this.bannerRule.style.fontSize)) || ruleFsMax; fs > ruleFsMin; fs -= 1) {
      if (this.bannerRule.height <= barH - 8) break;
      this.bannerRule.setFontSize(fs - 1);
    }

    // ── M1 (mock-2): COMPACT COACH SUBLINE INSIDE THE RULE BANNER ───────────────────────────────
    // The standing how-to guidance ("CHOMP IF SURE · SCAN IF UNSURE") is relocated OFF the floating
    // command-hint toast and INTO the rule banner, so the persistent coaching lives with the rule it
    // coaches. updateCommandHint reads the `hud.bannerCoachShown` registry flag and drops the DUPLICATE
    // resting line while it is up (contextual MOVE / NEXT TILE / SCAN verdicts + transient teach beats
    // still float). Renders ONLY when the rule is a single line AND the [rule + subline] pair fits the
    // FIXED bar height at the current text scale — otherwise the rule keeps the whole bar and the
    // floating hint is retained (graceful 150%-scale / long-rule fallback: never overflows the bar,
    // never pushes the grid). Static + reduceMotion-safe (no animation); scan-cyan, micro-tracked.
    let coachShown = false;
    if (this.bannerRule.getWrappedText(snap.rule).length <= 1) {
      const coachLine = this.scanUnlocked ? 'CHOMP IF SURE · SCAN IF UNSURE' : 'CHOMP IF SURE · OR MOVE ON';
      const sub = this.add
        .text(ruleX, cy, coachLine, {
          fontFamily: FONT.display,
          fontSize: `${typePx(this, 'caption', { vw: 0.024, min: 10, max: 13 })}px`,
          color: hex(PALETTE.scan),
          fontStyle: '600',
          align: 'left',
        })
        .setOrigin(0, 0.5)
        .setDepth(2);
      if (typeof (sub as unknown as { setLetterSpacing?: unknown }).setLetterSpacing === 'function') {
        sub.setLetterSpacing(0.6); // #51-style micro tracking so the coach line reads as arcade UI
      }
      // Horizontal fit: scale DOWN only, so the coach line never clips the pause button / gutter rail.
      if (sub.width > ruleAvail) sub.setScale(ruleAvail / sub.width);
      const gap = 3;
      const ruleH = this.bannerRule.height;
      const subH = sub.displayHeight;
      const totalH = ruleH + gap + subH;
      if (totalH <= barH - 6) {
        // Lift the inline [RULE]-tag + rule pair up so the whole [rule + coach] group centres in the
        // bar; the subline hangs below, aligned to the rule's left edge (ruleX).
        const dY = (gap + subH) / 2;
        this.bannerRule.setY(cy - dY);
        tagText.setY(cy - dY);
        tagG.setY(-dY); // graphics drawn at cy-relative coords → offset the whole shape up by dY
        sub.setY(cy - dY + ruleH / 2 + gap + subH / 2);
        coachShown = true;
      } else {
        sub.destroy(); // no vertical room (long / 2-line rule, or 150% text scale) → rule keeps the bar
      }
    }
    // Hand the flag to updateCommandHint (set every board build; read in the Game update loop only).
    this.registry.set('hud.bannerCoachShown', coachShown);

    // Pause iconButton docked at the bar's right edge (mirrors the ESC / P keys via openPause).
    this.buildPauseButton(barX + barW - pauseSize / 2 - 8, cy, pauseSize);
  }



  /**
   * The decorative atmosphere theme for this board. Deterministic: regular runs key off the
   * boardIndex rung (0..4); the Daily keys off the dailyDifficulty rung — its profile is the
   * "fairest fenced" curve (≈ rung 1), so it wears the indigo mood rather than always looking like
   * training board 0. Never randomized; never reads Core state.
   */
export function boardRung(this: GameScene): number {
    return this.isDaily ? 1 : Math.min(BOARD_THEMES.length - 1, Math.max(0, this.boardIndex));
  }



export function boardTheme(this: GameScene): BoardTheme {
    return BOARD_THEMES[this.boardRung()]!;
  }



  /**
   * PER-RUNG MOTIF FIELD — the "place" layer of the board-variety pass (hue wash + motif + frame
   * ornament). One sparse, build-once Graphics painted between the rung wash (depth -9.5) and every
   * board/HUD layer so the five rungs read as distinct VENUES, not tinted copies of one room:
   *   rung 0 deep blue — faint circuit traces (right-angle runs, node dots)   "the mainframe"
   *   rung 1 indigo    — scattered diamond studs                              "the vault"
   *   rung 2 violet    — thin diagonal streams with rare brighter pulses      "the datafall"
   *   rung 3 teal      — honeycomb hints (small hex clusters)                 "the hive"
   *   rung 4 ember     — rising ember pips, pooled low                        "the furnace"
   * HARD RULES (decoration only): placement is a pure hash of the rung (motifRand — no
   * Math.random); every element stays single-digit alpha over the wash; discrete glyphs are
   * SKIPPED inside the board-panel rect so pattern never sits under a tile label (rung 2's
   * full-field streams may cross BEHIND the panel — its 0.72 surface fill + opaque tile faces
   * occlude them to invisibility); gameplay state colors are untouched and every mark reuses the
   * rung's desaturated accent, so nothing here can read as a state signal. The only animation is
   * rung 4's ember drift: a transform-only tween on the build-once Graphics (~0.09 Hz, far under
   * the 0.5 Hz cap), motion-gated with the static field as the reduced-motion fallback. The daily
   * inherits rung 1's motif via boardRung(), exactly like its hue theme. Scene-owned objects +
   * scene-owned tween -> destroyed automatically on every board restart (no leaks).
   */
  /** The bucket accent for this board — feeds the GET READY card + board-frame tint so the chrome
   *  agrees with the topic world. Falls back to the science accent for unmatched topics. */
export function boardWorldAccent(this: GameScene): number {
    return WORLD_ACCENT[this.boardWorldBucket()];
  }


  /** Resolve this board's topic bucket from the topic slug + label (deterministic; no Core read). */
export function boardWorldBucket(this: GameScene): WorldBucket {
    return matchWorldBucket(`${this.topic ?? ''} ${this.topicLabel ?? ''}`) ?? WORLD_DEFAULT;
  }


  /**
   * TOPIC WORLD layer (S6) — the per-topic "place" behind the board. Painted at the paintBoardMotif
   * seam, UNDER a ~60% black legibility scrim (the composites proved 55% keeps every tile label ≥4.5:1;
   * we exceed it), BELOW the rung wash (-9.5) so the rung mood still tints the world, and far below the
   * board panel / grid / HUD — so a label can never sit on world detail. Cover-fits the orientation's
   * backdrop; a subtle ≤6px drift under motion only (reduceMotion holds it dead still). No world art
   * registered ⇒ no-op ⇒ the plain OLED stage shows (fallback contract). Scene-owned ⇒ auto-torn-down.
   */
export function paintBoardWorld(this: GameScene): void {
    const land = isLandscape();
    const bucket = this.boardWorldBucket();
    const key = land ? WORLD_TEX[bucket].L : WORLD_TEX[bucket].P;
    if (!hasTex(key)) return; // plain-stage fallback (unknown topic / headless decode)
    const W = this.scale.width;
    const H = this.scale.height;
    const motion = motionAllowed();
    const img = this.add.image(W / 2, H / 2, tex(key)).setDepth(-9.7).setScrollFactor(0);
    // Cover-fit (never letterbox); overscale a hair under motion so the drift never exposes an edge.
    const cover = Math.max(W / (img.width || 1), H / (img.height || 1)) * (motion ? 1.05 : 1);
    img.setScale(cover);
    // FLAT scrim — the label-legibility floor. Tile faces + the RULE bar are OPAQUE, so a label is never
    // at risk from the world regardless; this flat layer governs how much world reads in the open MARGINS.
    // M4/#27 lowered it (WORLD_SCRIM) so the world reads HARD at the edges/gutters. Above the world
    // (-9.6), below the rung wash (-9.5) which then adds the per-rung hue.
    this.add
      .rectangle(W / 2, H / 2, W, H, 0x05060c, WORLD_SCRIM[bucket])
      .setDepth(-9.6)
      .setScrollFactor(0);
    // M4/#27: FRAME the play area. Re-deepen ONLY the board region with a soft center-weighted dark POOL
    // (concentric fading ellipses pooled on the board centre), so the play column stays heavily scrimmed
    // + calm (immediate surround + RULE band ≥4.5:1, world texture never competes with the grid) while
    // the screen edges/gutters + the dead band above the RULE now show the brighter world. Above the flat
    // scrim (-9.6), below the rung wash (-9.5). Build-once static graphics; scene-owned ⇒ auto-torn-down.
    const bcx = this.gridX + this.boardW / 2;
    const bcy = this.gridY + (this.cellH * this.rows) / 2;
    const poolRx = this.boardW * 1.05;
    const poolRy = this.cellH * this.rows * 1.05;
    const pool = this.add.graphics().setDepth(-9.55).setScrollFactor(0);
    const poolLayers = 6;
    for (let i = poolLayers; i >= 1; i--) {
      pool.fillStyle(0x05060c, 0.038); // ~0.23 pooled at centre, tapering to 0 past the board rect
      pool.fillEllipse(bcx, bcy, poolRx * (i / poolLayers) * 2, poolRy * (i / poolLayers) * 2);
    }
    if (motion) {
      // Subtle slow parallax drift (≤6px), motion-gated. Reduced-motion holds the world dead still.
      this.tweens.add({
        targets: img,
        x: W / 2 + 6,
        y: H / 2 - 5,
        duration: 9000,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }


export function paintBoardMotif(this: GameScene): void {
    // S6: paint the topic WORLD first (deepest board layer, under the scrim + rung wash + motif).
    this.paintBoardWorld();
    const W = this.scale.width;
    const H = this.scale.height;
    const rung = this.boardRung();
    const accent = this.boardTheme().accent;
    // Exclusion rect: the board panel + accent hairline + a small margin. Discrete glyphs never
    // paint inside it, so a tile label can never sit on pattern.
    const boardH = this.cellH * this.rows;
    const pad = Math.max(8, Math.min(14, this.cellW * 0.12));
    const bx0 = this.gridX - pad - 8;
    const by0 = this.gridY - pad - 8;
    const bx1 = this.gridX + this.boardW + pad + 8;
    const by1 = this.gridY + boardH + pad + 8;
    const clearOfBoard = (x0: number, y0: number, x1: number, y1: number): boolean =>
      x1 < bx0 || x0 > bx1 || y1 < by0 || y0 > by1;
    // Tablet/desktop viewports have far more open margin around the board — scale the element
    // budget with area so the field stays evenly sparse instead of thinning out.
    const density = Math.max(1, Math.min(2.5, (W * H) / (360 * 640)));
    const r = (i: number): number => motifRand(rung, i);
    // Marks are drawn in a WHITE-LIFTED accent tint: alpha stays single-digit (the sparseness
    // contract) while the added luminance keeps the pattern legible over the near-black margins
    // that the -10 edge vignette leaves behind (raw accent at these alphas proved invisible at
    // 360x640 — first capture round).
    const tint = mixColor(accent, 0xffffff, 0.45);

    const g = this.add
      .graphics()
      .setDepth(-9.4) // above the rung wash (-9.5), below tension warmth (0.4) + all board/HUD layers
      .setScrollFactor(0)
      .setBlendMode(Phaser.BlendModes.ADD); // additive like the backdrop glows — luminous, not chalky

    if (rung === 0) {
      // CIRCUIT TRACES: L-shaped runs (horizontal leg, right-angle bend, short vertical leg) with a
      // node dot at each end. Hashed candidates; keep the first N that clear the board rect.
      let kept = 0;
      const want = Math.round(8 * density);
      for (let i = 0; i < 48 && kept < want; i++) {
        const x0 = W * (0.04 + 0.92 * r(i * 5 + 1));
        const y0 = H * (0.03 + 0.94 * r(i * 5 + 2));
        const x1 = x0 + (r(i * 5 + 3) - 0.5) * W * 0.5;
        const y1 = y0 + (r(i * 5 + 4) - 0.5) * H * 0.12;
        const minX = Math.min(x0, x1) - 3;
        const maxX = Math.max(x0, x1) + 3;
        const minY = Math.min(y0, y1) - 3;
        const maxY = Math.max(y0, y1) + 3;
        if (!clearOfBoard(minX, minY, maxX, maxY)) continue;
        kept++;
        g.lineStyle(1.5, tint, 0.08);
        g.beginPath();
        g.moveTo(x0, y0);
        g.lineTo(x1, y0);
        g.lineTo(x1, y1);
        g.strokePath();
        g.fillStyle(tint, 0.09);
        g.fillCircle(x0, y0, 2);
        g.fillCircle(x1, y1, 2);
      }
    } else if (rung === 1) {
      // DIAMOND STUDS: small rotated-square outlines, a minority filled as solid pips.
      let kept = 0;
      const want = Math.round(16 * density);
      for (let i = 0; i < 64 && kept < want; i++) {
        const x = W * (0.04 + 0.92 * r(i * 4 + 1));
        const y = H * (0.03 + 0.94 * r(i * 4 + 2));
        const s = 3 + 5.5 * r(i * 4 + 3);
        if (!clearOfBoard(x - s - 2, y - s - 2, x + s + 2, y + s + 2)) continue;
        kept++;
        g.beginPath();
        g.moveTo(x, y - s);
        g.lineTo(x + s, y);
        g.lineTo(x, y + s);
        g.lineTo(x - s, y);
        g.closePath();
        if (r(i * 4 + 4) < 0.3) {
          g.fillStyle(tint, 0.08);
          g.fillPath();
        } else {
          g.lineStyle(1.2, tint, 0.09);
          g.strokePath();
        }
      }
    } else if (rung === 2) {
      // DIAGONAL STREAMS: thin parallel lines falling down-right across the whole field (occluded
      // behind the opaque banner/panel; they read in the open margins), plus rare brighter pulse
      // dashes riding the same streams — pulses only where they clear the board.
      const slope = 0.5; // px right per px down
      const n = Math.round(11 * density);
      for (let i = 0; i < n; i++) {
        const xTop = W * (-0.55 + 1.75 * r(i * 3 + 1));
        const a = 0.05 + 0.04 * r(i * 3 + 2);
        g.lineStyle(r(i * 3 + 3) < 0.3 ? 2 : 1.2, tint, a);
        g.lineBetween(xTop, -8, xTop + slope * (H + 16), H + 8);
      }
      for (let i = 0; i < Math.round(8 * density); i++) {
        const xTop = W * (-0.55 + 1.75 * r(i * 3 + 1)); // anchor of stream i (same hash indices)
        const yP = H * (0.04 + 0.9 * r(i * 7 + 41));
        const len = 14;
        const xP = xTop + slope * (yP + 8);
        const xP2 = xP + slope * len;
        if (!clearOfBoard(xP - 2, yP - 2, xP2 + 2, yP + len + 2)) continue;
        g.lineStyle(2, tint, 0.09);
        g.lineBetween(xP, yP, xP2, yP + len);
      }
    } else if (rung === 3) {
      // HONEYCOMB HINTS: small clusters of a centre hex + 2-3 edge-tiled neighbours.
      const hexOutline = (hx: number, hy: number, hr: number, alpha: number): void => {
        g.lineStyle(1.2, tint, alpha);
        g.beginPath();
        for (let k = 0; k < 6; k++) {
          const ang = (Math.PI / 3) * k + Math.PI / 6; // pointy-top
          const px = hx + Math.cos(ang) * hr;
          const py = hy + Math.sin(ang) * hr;
          if (k === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.strokePath();
      };
      let kept = 0;
      const want = Math.round(5 * density);
      for (let i = 0; i < 36 && kept < want; i++) {
        const cxh = W * (0.06 + 0.88 * r(i * 6 + 1));
        const cyh = H * (0.05 + 0.9 * r(i * 6 + 2));
        const hr = 8 + 6 * r(i * 6 + 3);
        const span = hr * 3.2; // centre hex + one ring of neighbours
        if (!clearOfBoard(cxh - span, cyh - span, cxh + span, cyh + span)) continue;
        kept++;
        hexOutline(cxh, cyh, hr, 0.09);
        const nbrs = 2 + Math.floor(r(i * 6 + 4) * 2);
        const baseDir = Math.floor(r(i * 6 + 5) * 6);
        for (let k = 0; k < nbrs; k++) {
          const ang = (Math.PI / 3) * ((baseDir + k * 2) % 6); // distinct directions
          hexOutline(cxh + Math.cos(ang) * hr * Math.sqrt(3), cyh + Math.sin(ang) * hr * Math.sqrt(3), hr, 0.065);
        }
      }
    } else {
      // RISING EMBER PIPS: warm dots pooled toward the floor (bottom-biased hash), a few wearing a
      // soft halo. Under motion the whole field eases up a few px and settles (see doc above).
      let kept = 0;
      const want = Math.round(22 * density);
      for (let i = 0; i < 88 && kept < want; i++) {
        const x = W * (0.04 + 0.92 * r(i * 5 + 1));
        const t = r(i * 5 + 2);
        const y = H * (0.05 + 0.92 * (1 - t * t)); // t^2 squashes upward -> embers pool low
        const rad = 1.2 + 1.6 * r(i * 5 + 3);
        if (!clearOfBoard(x - rad - 2, y - rad - 2, x + rad + 2, y + rad + 2)) continue;
        kept++;
        g.fillStyle(tint, 0.06 + 0.03 * r(i * 5 + 4));
        g.fillCircle(x, y, rad);
        if (r(i * 5 + 5) < 0.3) {
          g.fillStyle(tint, 0.035);
          g.fillCircle(x, y, rad * 2.8);
        }
      }
      if (motionAllowed()) {
        this.tweens.add({
          targets: g,
          y: -7,
          alpha: { from: 1, to: 0.82 },
          duration: 5600,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
      }
    }
  }



  /**
   * PER-RUNG MOTIF, frame echo: a tiny ornament on each corner of the board frame (circuit node /
   * diamond stud / stream chamfer / hex cell / ember trail) so the rung's "place" identity also
   * reads right at the board. Decoration only — it lives in the frame-corner gutter (the tile
   * faces start >= pad+2 inside the panel, beyond every glyph's reach) and reuses the same
   * desaturated hairline accent, so it can never be mistaken for a state signal.
   */
export function paintFrameCornerMotif(this: GameScene, cx: number, cy: number, frameW: number, frameH: number, accent: number): void {
    const rung = this.boardRung();
    // 2.2: above the panel fill (2) so the 0.72 surface doesn't dim it, below the cells (3).
    const g = this.add.graphics().setDepth(2.2);
    for (const sx of [-1, 1] as const) {
      for (const sy of [-1, 1] as const) {
        // nudge 2px inward so edge-tight phone layouts don't clip the glyph at the screen edge
        const x = cx + sx * (frameW / 2 - 2);
        const y = cy + sy * (frameH / 2 - 2);
        switch (rung) {
          case 0: // circuit node: corner dot + two trace stubs running inward along the frame edges
            g.fillStyle(accent, 0.6);
            g.fillCircle(x, y, 2.2);
            g.lineStyle(1.5, accent, 0.45);
            g.lineBetween(x, y, x - sx * 12, y);
            g.lineBetween(x, y, x, y - sy * 12);
            break;
          case 1: // diamond stud
            g.lineStyle(1.5, accent, 0.58);
            g.beginPath();
            g.moveTo(x, y - 5.5);
            g.lineTo(x + 5.5, y);
            g.lineTo(x, y + 5.5);
            g.lineTo(x - 5.5, y);
            g.closePath();
            g.strokePath();
            break;
          case 2: // stream chamfer: two short parallel diagonals cutting across the corner
            g.lineStyle(1.5, accent, 0.5);
            g.lineBetween(x - sx * 9, y, x, y - sy * 9);
            g.lineBetween(x - sx * 15, y, x, y - sy * 15);
            break;
          case 3: { // hex cell
            g.lineStyle(1.5, accent, 0.55);
            g.beginPath();
            for (let k = 0; k < 6; k++) {
              const ang = (Math.PI / 3) * k + Math.PI / 6;
              const px = x + Math.cos(ang) * 6;
              const py = y + Math.sin(ang) * 6;
              if (k === 0) g.moveTo(px, py);
              else g.lineTo(px, py);
            }
            g.closePath();
            g.strokePath();
            break;
          }
          default: // ember trail: three pips fading as they rise inward off the corner
            g.fillStyle(accent, 0.65);
            g.fillCircle(x, y, 2.2);
            g.fillStyle(accent, 0.48);
            g.fillCircle(x - sx * 5, y - sy * 6, 1.5);
            g.fillStyle(accent, 0.34);
            g.fillCircle(x - sx * 9, y - sy * 12, 1.1);
            break;
        }
      }
    }
  }
